import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../Contexts/AuthContext/AuthContext";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { toast } from "react-toastify";
import { baseUrl } from "../../../../Environments/environment";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import { Switch } from "@headlessui/react";
import { FaTrash } from "react-icons/fa";
import { CgChevronDown, CgChevronUp } from "react-icons/cg";
import { apiClient } from "../../../../api/index.js";
import { Info } from "lucide-react";

const ConfirmModal = ({ open, review, onConfirm, onCancel }) => {
	if (!open || !review) return null;
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
			<div className="bg-white w-full max-w-md rounded-lg shadow-xl p-4 sm:p-6">
				<h3 className="text-base sm:text-lg font-semibold mb-2">
					Are you sure want to delete this review job?
				</h3>
				<div className="mb-4 text-gray-700 text-sm">
					<span className="font-semibold ">Network:</span> {review?.network}
					<br />
					<span className="font-semibold ">Review Job ID:</span>{" "}
					{review?.reviewJobId}
				</div>
				<div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
					<button
						onClick={onCancel}
						className="px-4 py-2 text-sm sm:text-base rounded bg-gray-200 hover:bg-gray-300 w-full sm:w-auto"
					>
						No
					</button>
					<button
						onClick={onConfirm}
						className="px-4 py-2 text-sm sm:text-base rounded bg-indigo-600 text-white hover:bg-indigo-700 w-full sm:w-auto"
					>
						Yes
					</button>
				</div>
			</div>
		</div>
	);
};

export default function PlatformIntegration() {
	const { accessToken } = useAuth();
	const { selectedPropertyId } = useProperty();
	const [loading, setLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [workflowData, setWorkflowData] = useState(null);
	const [isExpandedReviews, setIsExpandedReviews] = useState(true);

	const [reviewsFlow, setReviewsFlow] = useState({
		reviewsEnabled: false,
		reviewsLink: "",
		startLimitForReviewLink: 1,
	});

	const [reviewsJobFlow, setReviewsJobFlow] = useState({
		reviewJobs: [],
	});

	const [isAddingReviewJob, setIsAddingReviewJob] = useState(false);
	const [isUpdatingReviewJob, setIsUpdatingReviewJob] = useState(false);
	const [editingJobId, setEditingJobId] = useState(null);
	const [newReviewJob, setNewReviewJob] = useState({
		network: "",
		networkUrl: "",
		redirectUrl: "",
	});
	const [editingReviewJob, setEditingReviewJob] = useState({
		network: "",
		networkUrl: "",
		redirectUrl: "",
	});

	const [confirmModal, setConfirmModal] = useState({
		open: false,
		review: null,
	});

	const availableNetworks = [
		{ value: "google", label: "Google" },
		{ value: "tripadvisor", label: "TripAdvisor" },
		{ value: "yelp", label: "Yelp" },
		{ value: "booking", label: "Booking.com" },
		{ value: "expedia", label: "Expedia" },
		{ value: "agoda", label: "Agoda" },
	];

	const redirectOptions = [
		{ label: "Greater than 1", value: "1" },
		{ label: "Greater than 2", value: "2" },
		{ label: "Greater than 3", value: "3" },
		{ label: "Greater than 4", value: "4" },
	];
	const [syncingWithZembra, setSyncingWithZembra] = useState(false);

	const getAvailableNetworks = () => {
		const usedNetworks = reviewsJobFlow.reviewJobs.map((job) => job.network);
		return availableNetworks.filter(
			(network) => !usedNetworks.includes(network.value),
		);
	};

	const getAvailableNetworksForEdit = (currentNetwork) => {
		const usedNetworks = reviewsJobFlow.reviewJobs
			.filter((job) => job._id !== editingJobId)
			.map((job) => job.network);
		return availableNetworks.filter(
			(network) =>
				!usedNetworks.includes(network.value) ||
				network.value === currentNetwork,
		);
	};

	const toggleExpandReviews = () => setIsExpandedReviews(!isExpandedReviews);

	const fetchWorkflow = async () => {
		setLoading(true);
		try {
			const response = await apiClient.get(
				`${baseUrl}/workflow/${selectedPropertyId}`,
			);
			if (response.ok) {
				const newToken = response.headers.get("Authorization");
				if (newToken) {
					localStorage.setItem("accessToken", newToken.split(" ")[1]);
				}
			}
			const data = response.data.result;
			setWorkflowData(data);

			setReviewsFlow({
				reviewsEnabled: data.reviewFlow && data.reviewFlow.reviewsEnabled,
				reviewsLink: data.reviewFlow && data.reviewFlow.reviewsLink,
				startLimitForReviewLink:
					data.reviewFlow && data.reviewFlow.startLimitForReviewLink,
			});
		} catch (error) {
			const errorMessage =
				error.response?.data?.message ||
				"Failed to fetch workflow data. Please try again.";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const fetchReviewJobs = async () => {
		try {
			const token = localStorage.getItem("accessToken");
			const response = await apiClient.get(
				`${baseUrl}/reviews/jobs/${selectedPropertyId}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);
			if (response.data.statusCode === 200) {
				setReviewsJobFlow({
					reviewJobs: response.data.result.reviewJobs || [],
				});
			}
		} catch (error) {
			console.error("Error fetching review jobs:", error);
		}
	};

	useEffect(() => {
		if (selectedPropertyId) {
			fetchWorkflow();
			fetchReviewJobs();
		}
	}, [accessToken, selectedPropertyId]);

	const handleSaveChanges = async () => {
		setIsLoading(true);
		try {
			const formData = new FormData();

			const addOnsFlowData = workflowData?.addOnsFlow || {
				customAddOns: [],
				checkInOutAddOns: [],
				houseKeepingAddOns: { name: "House Keeping", enabled: false, default: true, options: [] },
				upgradeRoom: { name: "Upgrade Room", default: true, image: [], options: [], tiers: [] }
			};

			formData.append("addOnsFlow", JSON.stringify(addOnsFlowData));
			formData.append("reviewFlow", JSON.stringify({ ...reviewsFlow }));

			await apiClient.put(
				`${baseUrl}/workflow/${selectedPropertyId}`,
				formData,
			);

			toast.success("Workflow updated successfully!");
			fetchWorkflow();
		} catch (error) {
			console.error("Error updating workflow reviews flow:", error);
			const errorMessage =
				error.response?.data?.message ||
				"Failed to update workflow. Please try again.";
			toast.error(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleAddReviewJob = async () => {
		if (
			!newReviewJob.network ||
			!newReviewJob.networkUrl ||
			!newReviewJob.redirectUrl
		) {
			toast.error("Please fill in all fields");
			return;
		}

		try {
			setIsAddingReviewJob(true);
			const token = localStorage.getItem("accessToken");

			const response = await apiClient.post(
				`${baseUrl}/reviews/jobs/${selectedPropertyId}`,
				{
					network: newReviewJob.network,
					networkUrl: newReviewJob.networkUrl,
					redirectUrl: newReviewJob.redirectUrl,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			if (response.data.statusCode === 201) {
				toast.success("Review job added successfully!");
				setNewReviewJob({
					network: "",
					networkUrl: "",
					redirectUrl: "",
				});
				fetchReviewJobs();
			}
		} catch (error) {
			console.error("Error adding review job:", error);
			const errorMessage =
				error.response?.data?.message || "Failed to add review job";
			toast.error(errorMessage);
		} finally {
			setIsAddingReviewJob(false);
		}
	};

	const handleUpdateReviewJob = async () => {
		if (!editingReviewJob.redirectUrl) {
			toast.error("Please fill in all fields");
			return;
		}

		try {
			setIsUpdatingReviewJob(true);
			const token = localStorage.getItem("accessToken");

			const response = await apiClient.put(
				`${baseUrl}/reviews/jobs/${selectedPropertyId}/${editingJobId}`,
				{
					redirectUrl: editingReviewJob.redirectUrl,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			if (response.data.statusCode === 200) {
				toast.success("Review job updated successfully!");
				setEditingJobId(null);
				setEditingReviewJob({
					network: "",
					networkUrl: "",
					redirectUrl: "",
				});
				fetchReviewJobs();
			}
		} catch (error) {
			console.error("Error updating review job:", error);
			const errorMessage =
				error.response?.data?.message || "Failed to update review job";
			toast.error(errorMessage);
		} finally {
			setIsUpdatingReviewJob(false);
		}
	};

	const handleEditReviewJob = (job) => {
		setEditingJobId(job._id);
		setEditingReviewJob({
			network: job.network,
			networkUrl: job.networkUrl,
			redirectUrl: job.redirectUrl,
		});
	};


	const handleCancelEdit = () => {
		setEditingJobId(null);
		setEditingReviewJob({
			network: "",
			networkUrl: "",
			redirectUrl: "",
		});
	};

	const handleDeleteReviewJob = async () => {
		try {
			const review = confirmModal.review;
			setConfirmModal({ open: false, review: null });
			const jobId = review._id;
			if (jobId) {
				const token = localStorage.getItem("accessToken");

				const response = await apiClient.delete(
					`${baseUrl}/reviews/jobs/${selectedPropertyId}/${jobId}`,
					{
						headers: { Authorization: `Bearer ${token}` },
					},
				);

				if (response.data.statusCode === 200) {
					toast.success("Review job deleted successfully!");
					fetchReviewJobs();
				}
			}
		} catch (error) {
			console.error("Error deleting review job:", error);
			const errorMessage =
				error.response?.data?.message || "Failed to delete review job";
			toast.error(errorMessage);
		}
	};

	const handleSyncWithZembra = async (job) => {
		try {
			setSyncingWithZembra(true);
			const token = localStorage.getItem("accessToken");
			const response = await apiClient.post(
				`${baseUrl}/reviews/jobs/sync/${selectedPropertyId}/${job._id}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);
			if (response.data.statusCode === 200) {
				toast.success("Review job synced with Zembra successfully!");
				fetchReviewJobs();
			}
		} catch (error) {
			console.error("Error syncing with Zembra:", error);
			const errorMessage =
				error.response?.data?.result?.error || "Failed to Connect with Zembra. Please try again.";
			toast.error(errorMessage || "Failed to connect with Zembra. Please try again.");
		} finally {
			setSyncingWithZembra(false);
		}
	};

	return (
		<>
			{loading && <LoadingSpinner />}
			<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sticky top-0 bg-gray-100 z-10 py-3">
					<h2 className="text-2xl font-semibold">Platform Integration</h2>

					<div className="flex flex-col gap-2 sm:flex-row sm:gap-0 w-full sm:w-auto">
						<button
							onClick={handleSaveChanges}
							className="bg-green-400 hover:bg-green-500 text-white font-medium px-2 py-1.5 rounded-md sm:m-2 w-full sm:w-36"
						>
							{isLoading ? "Saving..." : "Save Changes"}
						</button>
					</div>
				</div>

				{/* Reviews + Reviews Job Flow (Matching exact original design format) */}
				<div className="bg-white shadow-md rounded-lg p-4 sm:p-6 w-full mb-4 sm:mb-6">
					<h3 className="text-lg sm:text-xl font-semibold mb-2">Reviews</h3>
					<p className="text-gray-700 text-sm sm:text-base">
						Know what guests are saying about your property !!
					</p>

					<div className="flex justify-end mt-4 bg-gray-50 p-2 rounded-md">
						<button
							onClick={toggleExpandReviews}
							className="text-indigo-500 hover:text-indigo-700 flex items-center text-sm"
						>
							{isExpandedReviews ? (
								<>
									View Less <CgChevronUp className="ml-1" />
								</>
							) : (
								<>
									View All <CgChevronDown className="ml-1" />
								</>
							)}
						</button>
					</div>

					{isExpandedReviews && (
						<div className="mt-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
							<div className="mb-4 mt-4">
								<div className="mb-2 flex flex-wrap sm:flex-nowrap items-center justify-between gap-3">
									<p className="font-bold text-sm sm:text-base">
										When to redirect guest to google reviews?
									</p>
									<Switch
										checked={reviewsFlow.reviewsEnabled}
										onChange={(checked) =>
											setReviewsFlow((prev) => ({
												...prev,
												reviewsEnabled: checked,
											}))
										}
										className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 data-[checked]:bg-indigo-600"
									>
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
										/>
									</Switch>
								</div>

								<select
									value={
										reviewsFlow.reviewsEnabled
											? reviewsFlow.startLimitForReviewLink
											: ""
									}
									onChange={(e) =>
										setReviewsFlow((prev) => ({
											...prev,
											startLimitForReviewLink: e.target.value,
										}))
									}
									disabled={!reviewsFlow.reviewsEnabled}
									className="border border-gray-300 rounded-md p-2 text-sm sm:text-base w-full disabled:bg-gray-100 disabled:text-gray-500"
								>
									{!reviewsFlow.reviewsEnabled && (
										<option value="">Turn on Reviews to enable redirect</option>
									)}
									{redirectOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>

							<div className="mt-5 border-t border-gray-200 pt-4">
								<h4 className="text-base sm:text-lg font-semibold mb-1">
									Reviews Job Flow
								</h4>
								<p className="text-gray-700 text-sm sm:text-base">
									Manage review jobs for different networks. Add review page URLs
									for each platform.
								</p>
							</div>

							<div className="mt-4 bg-white p-3 sm:p-4 rounded-lg">
								{/* Add Review Job Form */}
								<div className="mb-4 sm:mb-6">
									<h4 className="text-gray-700 font-bold mb-3 text-sm sm:text-base">
										Add New Review Job
									</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Network
											</label>
											<select
												value={newReviewJob.network}
												onChange={(e) =>
													setNewReviewJob({
														...newReviewJob,
														network: e.target.value,
													})
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
											>
												<option value="">Select Network</option>
												{getAvailableNetworks().map((network) => (
													<option key={network.value} value={network.value}>
														{network.label}
													</option>
												))}
											</select>
										</div>
										<div>
											<div className="relative group cursor-pointer">
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Review Page URL{" "}
													<Info size={14} className="ml-1 inline-block" />
												</label>
												<div className="absolute left-0 sm:left-24 top-full mt-1 w-72 max-w-[calc(100vw-3rem)] sm:max-w-xs bg-gray-800 text-white text-xs rounded px-2 py-2.5 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-normal shadow-lg">
													Paste Actual Platform review page url here
													<br />
													{newReviewJob.network ? "For Example:" : ""}
													{newReviewJob.network === "tripadvisor"
														? "https://www.tripadvisor.in/Hotel_Review-g60713-d12290636-Reviews-Warfield_Hotel-San_Francisco_California.html"
														: newReviewJob.network === "google"
															? "https://www.google.com/maps?cid=472717649119152494"
															: newReviewJob.network === "booking"
																? "https://www.booking.com/hotel/us/warfield.en-gb.html"
																: newReviewJob.network === "expedia"
																	? "https://www.expedia.co.in/San-Francisco-Hotels-Warfield-Hotel.h17383168.Hotel-Information"
																	: newReviewJob.network === "agoda"
																		? "https://www.agoda.com/warfield-hotel_2/hotel/san-francisco-ca-us.html"
																		: newReviewJob.network === "yelp"
																			? "https://www.yelp.com/biz/warfield-hotel-san-francisco"
																			: ""}
												</div>
											</div>

											<input
												type="url"
												placeholder="https://example.com/reviews"
												value={newReviewJob.networkUrl}
												onChange={(e) =>
													setNewReviewJob({
														...newReviewJob,
														networkUrl: e.target.value,
													})
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
											/>
										</div>
										<div className="md:col-span-2">
											<div className="relative group cursor-pointer">
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Redirect URL{" "}
													<Info size={14} className="ml-1 inline-block" />
												</label>
												<div className="absolute left-0 sm:left-24 top-full mt-1 w-72 max-w-[calc(100vw-3rem)] sm:max-w-xs bg-gray-800 text-white text-xs rounded px-2 py-2.5 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-normal shadow-lg">
													Paste Link where you want customer to navigate
												</div>
											</div>

											<input
												type="url"
												placeholder="https://example.com/redirect/reviews"
												value={newReviewJob.redirectUrl}
												onChange={(e) =>
													setNewReviewJob({
														...newReviewJob,
														redirectUrl: e.target.value,
													})
												}
												className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
											/>
										</div>
									</div>
									<button
										onClick={handleAddReviewJob}
										disabled={
											isAddingReviewJob ||
											!newReviewJob.network ||
											!newReviewJob.networkUrl ||
											!newReviewJob.redirectUrl
										}
										className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 text-sm sm:text-base rounded-md transition-colors w-full sm:w-auto"
									>
										{isAddingReviewJob ? "Adding..." : "Add Review Job"}
									</button>
								</div>

								{/* Review Jobs List */}
								<div>
									<h4 className="text-gray-700 font-bold mb-3 text-sm sm:text-base">
										Existing Review Jobs
									</h4>
									{reviewsJobFlow.reviewJobs.length === 0 ? (
										<p className="text-gray-500 text-sm">
											No review jobs added yet.
										</p>
									) : (
										<div className="space-y-3">
											{reviewsJobFlow.reviewJobs.map((job) => (
												<div
													key={job._id || job.id}
													className="bg-white rounded-md border p-3 sm:p-4"
												>
													{editingJobId === job._id ? (
														// Edit Mode
														<div className="space-y-3">
															<div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-1">
																		Network
																	</label>
																	<select
																		value={editingReviewJob.network}
																		disabled={true}
																		className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
																	>
																		<option value={job.network}>{job.network}</option>
																	</select>
																</div>
																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-1">
																		Redirect URL
																	</label>
																	<input
																		type="url"
																		placeholder="https://example.com/redirect/reviews"
																		value={editingReviewJob.redirectUrl}
																		onChange={(e) =>
																			setEditingReviewJob({
																				...editingReviewJob,
																				redirectUrl: e.target.value,
																			})
																		}
																		className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
																	/>
																</div>
															</div>
															<div className="flex flex-col sm:flex-row gap-2">
																<button
																	onClick={handleUpdateReviewJob}
																	disabled={
																		isUpdatingReviewJob ||
																		!editingReviewJob.redirectUrl
																	}
																	className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm w-full sm:w-auto"
																>
																	{isUpdatingReviewJob
																		? "Updating..."
																		: "Save Changes"}
																</button>
																<button
																	onClick={handleCancelEdit}
																	disabled={isUpdatingReviewJob}
																	className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm w-full sm:w-auto"
																>
																	Cancel
																</button>
															</div>
														</div>
													) : (
														// View Mode
														<div>
															<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
																<div className="flex flex-wrap items-center gap-2 sm:gap-3">
																	<span className="font-medium text-gray-900 capitalize text-base sm:text-lg">
																		{job.network}
																	</span>
																	{/* <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
																		{job.reviewJobId || "No Job ID"}
																	</span> */}
																	{job.network === "google" && !job.reviewJobId && (<button
																		onClick={() => handleSyncWithZembra(job)}
																		className="text-blue-500 hover:text-blue-700 p-1 flex items-center gap-1 text-sm font-medium"
																		title="Connect With Zembra"
																		disabled={syncingWithZembra}
																	>
																		<svg
  className="w-4 h-4"
  fill="none"
  stroke="currentColor"
  viewBox="0 0 24 24"
>
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
    d="M4 4v5h5M20 20v-5h-5M20 9A8 8 0 005.64 5.64L4 9m0 6a8 8 0 0014.36 4.36L20 15"
  />
</svg>
																		<span>{syncingWithZembra ? "Connecting..." : "Connect With Zembra"}</span>
																	</button>)}
																</div>
																<div className="flex items-center justify-between sm:justify-start gap-4 w-full sm:w-auto mt-2 sm:mt-0">
															
																
																<button
  onClick={() => handleEditReviewJob(job)}
  disabled={true}
  className="text-blue-500 hover:text-blue-700 p-1 flex items-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-blue-500"
  title="Edit review job"
>
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
  <span>Edit</span>
</button>

<button
  onClick={() => setConfirmModal({ open: true, review: job })}
  disabled={true}
  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-red-500 disabled:hover:bg-transparent"
  title="Delete review job"
>
  <FaTrash className="w-4 h-4" />
</button>
																</div>
															</div>

															<div className="border-t border-gray-100 pt-3 mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
																<div>
																	<span className="font-medium text-gray-700">
																		Review Page URL:
																	</span>
																	<p className="text-gray-600 break-all text-xs sm:text-sm">
																		{job.networkUrl || "Not provided"}
																	</p>
																</div>
																<div>
																	<span className="font-medium text-gray-700">
																		UID:
																	</span>
																	<p className="text-gray-600 font-mono text-xs break-all">
																		{job.uid || "Not available"}
																	</p>
																</div>
																<div>
																	<span className="font-medium text-gray-700">
																		Slug:
																	</span>
																	<p className="text-gray-600 font-mono text-xs break-all">
																		{job.slug || "Not available"}
																	</p>
																</div>
																{/* <div>
																	<span className="font-medium text-gray-700">
																		Job ID:
																	</span>
																	<p className="text-gray-600 font-mono text-xs break-all">
																		{job.reviewJobId || "Not available"}
																	</p>
																</div> */}
																<div>
																	<span className="font-medium text-gray-700">
																		Redirect URL:
																	</span>
																	<p className="text-gray-600 break-all text-xs sm:text-sm">
																		{job.redirectUrl || "Not provided"}
																	</p>
																</div>
															</div>
														</div>
													)}
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			<ConfirmModal
				open={confirmModal.open}
				review={confirmModal.review}
				onConfirm={handleDeleteReviewJob}
				onCancel={() => setConfirmModal({ open: false, review: null })}
			/>
		</>
	);
}
